import type { ModelCalculationParamSelection, ModelParamDefinition } from '../types/modelParam'

export function getApplicationModelParamKeys(definitions: ModelParamDefinition[]): string[] {
  return definitions
    .filter((definition) => definition.scope === 'application')
    .map((definition) => definition.key)
}

export function getApplicantModelParamKeys(definitions: ModelParamDefinition[]): string[] {
  return definitions
    .filter((definition) => definition.scope === 'applicant')
    .map((definition) => definition.key)
}

export function createDefaultModelCalculationParamSelection(
  definitions: ModelParamDefinition[],
): ModelCalculationParamSelection {
  return definitions.reduce<ModelCalculationParamSelection>((selection, definition) => {
    selection[definition.key] = false
    return selection
  }, {})
}

export function getParamSelectionFromForm(
  raw: Record<string, unknown>,
  definitions: ModelParamDefinition[],
): ModelCalculationParamSelection {
  const selection = { ...createDefaultModelCalculationParamSelection(definitions) }

  Object.keys(selection).forEach((key) => {
    selection[key] = !!raw[key]
  })

  return selection
}

export function buildModelParamsPayload(
  selection: ModelCalculationParamSelection,
  definitions: ModelParamDefinition[],
): unknown[] {
  const params: unknown[] = []
  const applicationModelParamKeys = getApplicationModelParamKeys(definitions)
  const applicantModelParamKeys = getApplicantModelParamKeys(definitions)

  applicationModelParamKeys.forEach((field) => {
    if (selection[field]) {
      params.push(field)
    }
  })

  const applicantFields: string[] = []
  applicantModelParamKeys.forEach((field) => {
    if (selection[field]) {
      applicantFields.push(field)
    }
  })

  if (applicantFields.length) {
    params.push({ applicants: applicantFields })
  }

  return params
}

export function isSameModelParamSelection(
  a: ModelCalculationParamSelection,
  b: ModelCalculationParamSelection,
  definitions: ModelParamDefinition[],
): boolean {
  return definitions.every((definition) => !!a[definition.key] === !!b[definition.key])
}

export function parseModelParams(
  params: unknown,
  definitions: ModelParamDefinition[],
): ModelCalculationParamSelection {
  const selection = { ...createDefaultModelCalculationParamSelection(definitions) }
  const applicationModelParamKeys = getApplicationModelParamKeys(definitions)
  const applicantModelParamKeys = getApplicantModelParamKeys(definitions)

  if (!params) {
    return selection
  }

  let parsed: unknown = params
  if (typeof params === 'string') {
    try {
      parsed = JSON.parse(params)
    } catch {
      return selection
    }
  }

  if (!Array.isArray(parsed)) {
    return selection
  }

  parsed.forEach((item) => {
    if (item === 'number_of_applicants') {
      selection.applicant_count = true
    } else if (typeof item === 'string' && applicationModelParamKeys.includes(item)) {
      selection[item] = true
    } else if (item && typeof item === 'object' && Array.isArray((item as { applicants?: string[] }).applicants)) {
      ;(item as { applicants: string[] }).applicants.forEach((field) => {
        if (applicantModelParamKeys.includes(field)) {
          selection[field] = true
        }
      })
    }
  })

  return selection
}

export function hasAnyModelParamSelected(
  selection: ModelCalculationParamSelection,
  definitions: ModelParamDefinition[],
): boolean {
  return definitions.some((definition) => !!selection[definition.key])
}

function getDefinitionValueType(
  field: string,
  definitions: ModelParamDefinition[],
): ModelParamDefinition['valueType'] {
  const definition = definitions.find((item) => item.key === field)
  return definition?.valueType ?? 'number'
}

function coerceTestValue(field: string, value: unknown, definitions: ModelParamDefinition[]): unknown {
  if (value === '' || value == null) {
    return undefined
  }

  const valueType = getDefinitionValueType(field, definitions)

  if (valueType === 'string') {
    return String(value)
  }

  if (valueType === 'boolean') {
    return value === true || value === 'true' || value === '1' || value === 1
  }

  if (valueType === 'json') {
    try {
      return typeof value === 'string' ? JSON.parse(value) : value
    } catch {
      return value
    }
  }

  return Number(value)
}

export function buildTestParamsPayload(
  selection: ModelCalculationParamSelection,
  formValue: {
    applicant_count?: number | string
    applicants?: Array<Record<string, unknown>>
    [key: string]: unknown
  },
  definitions: ModelParamDefinition[],
): Record<string, unknown> {
  const params: Record<string, unknown> = {}
  const applicationModelParamKeys = getApplicationModelParamKeys(definitions)
  const applicantModelParamKeys = getApplicantModelParamKeys(definitions)

  applicationModelParamKeys.forEach((field) => {
    const value = coerceTestValue(field, formValue[field], definitions)
    if (selection[field] && value !== undefined) {
      params[field] = value
    }
  })

  const hasApplicantFields = applicantModelParamKeys.some((field) => selection[field])
  const applicantCount = selection.applicant_count
    ? Math.max(1, Number(formValue.applicant_count) || 1)
    : 1

  if (hasApplicantFields) {
    params.applicants = Array.from({ length: applicantCount }, (_, index) => {
      const applicant = formValue.applicants?.[index] ?? {}
      const item: Record<string, unknown> = {}

      applicantModelParamKeys.forEach((field) => {
        const value = coerceTestValue(field, applicant[field], definitions)
        if (selection[field] && value !== undefined) {
          item[field] = value
        }
      })

      return item
    })
  }

  return params
}

export function getParamInputType(field: string, definitions: ModelParamDefinition[]): string {
  const valueType = getDefinitionValueType(field, definitions)

  if (valueType === 'boolean') {
    return 'checkbox'
  }

  if (valueType === 'json') {
    return 'text'
  }

  if (valueType === 'string' && field === 'created_at') {
    return 'datetime-local'
  }

  if (valueType === 'number') {
    return 'number'
  }

  return 'text'
}

export function getParamPlaceholder(field: string, definitions: ModelParamDefinition[]): string {
  if (getDefinitionValueType(field, definitions) === 'json') {
    return 'Enter JSON value'
  }

  return `Enter ${field}`
}
